"use client"

import React, { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Box, Flex, Card, Heading, Text, TextField, Button, Checkbox, Separator, Callout } from "@radix-ui/themes"
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  AlertCircle,
  CheckCircle
} from "lucide-react"
import { restaurantAuthAPI } from "../../services/restaurantApiClient"
import { useRestaurantAuth } from "../../contexts/RestaurantAuthContext"
import { useLanguage } from "../../contexts/LanguageContext"
import TurboLogo from "../../components/TurboLogo"
import LanguageSwitcher from "../../components/LanguageSwitcher"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"

interface LoginFormData {
  email: string
  password: string
}

const RestaurantLoginPage: React.FC = () => {
  const navigate = useNavigate()
  const { login, isAuthenticated, user, loading: authLoading } = useRestaurantAuth()
  const { t, language, dir } = useLanguage()
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
  })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      navigate(`/restaurant/${user.restaurant._id}/dashboard`)
    }
  }, [authLoading, isAuthenticated, user, navigate])

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ minHeight: "100vh", background: "linear-gradient(135deg, var(--red-2) 0%, var(--indigo-2) 100%)" }} dir={dir}>
        <LoadingSpinner size="3" text={t('checkingAuthentication')} />
      </Flex>
    )
  }

  // Don't render the form if already authenticated
  if (isAuthenticated && user) {
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    setSuccess("")

    try {
      const response = await restaurantAuthAPI.login(formData)
      
      setSuccess(t('loginSuccessful'))
      
      // Ensure owner has isOwner flag set
      const ownerUser = {
        ...response.data.user,
        isOwner: true,
        role: response.data.user.role || "restaurant_owner"
      }
      
      // Use the restaurant auth context to login
      login(response.data.token, ownerUser)
      
      // Redirect to restaurant dashboard
      setTimeout(() => {
        navigate(`/restaurant/${response.data.user.restaurant._id}/dashboard`)
      }, 1000)
    } catch (error: any) {
      console.error("Login error:", error)
      // Extract error message from response
      const errorMessage = error?.response?.data?.message || error?.userMessage || error?.message || t('loginFailed')
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

    return (
      <Flex direction="column" align="center" justify="center" p="4" style={{ minHeight: "100vh", background: "linear-gradient(135deg, var(--red-2) 0%, var(--indigo-2) 100%)" }} dir={dir}>
        {/* Language Switcher */}
        <Box style={{ position: "absolute", top: 16, [language === 'ar' ? 'left' : 'right']: 16 }}>
          <LanguageSwitcher />
        </Box>
        
        <Flex direction="column" gap="6" style={{ width: "100%", maxWidth: 448 }}>
          <Flex direction="column" align="center" gap="4">
            <Box>
              <TurboLogo width={64} height={54} color="#DC2626" />
            </Box>
            <Heading size="8" align="center">
              {t('restaurantPortal')}
            </Heading>
            <Text size="3" color="gray" align="center">
              {t('signInToManage')}
            </Text>
          </Flex>

          <Card size="4">
            {error && (
              <Callout.Root color="red" mb="4">
                <Callout.Icon>
                  <AlertCircle size={20} />
                </Callout.Icon>
                <Callout.Text>{error}</Callout.Text>
              </Callout.Root>
            )}

            {success && (
              <Callout.Root color="green" mb="4">
                <Callout.Icon>
                  <CheckCircle size={20} />
                </Callout.Icon>
                <Callout.Text>{success}</Callout.Text>
              </Callout.Root>
            )}

            <form onSubmit={handleSubmit}>
              <Flex direction="column" gap="4">
                <Box>
                  <Text as="label" size="2" weight="medium" mb="2">
                    {t('emailAddress')}
                  </Text>
                  <TextField.Root
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('enterYourEmail')}
                    size="3"
                  >
                    <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                      <Mail size={18} />
                    </TextField.Slot>
                  </TextField.Root>
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium" mb="2">
                    {t('password')}
                  </Text>
                  <TextField.Root
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={t('enterYourPassword')}
                    size="3"
                  >
                    <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                      <Lock size={18} />
                    </TextField.Slot>
                    <TextField.Slot side={language === 'ar' ? 'left' : 'right'}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="1"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </Button>
                    </TextField.Slot>
                  </TextField.Root>
                </Box>

                <Flex align="center" justify="between" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
                  <Text as="label" size="2">
                    <Flex align="center" gap="2" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
                      <Checkbox id="remember-me" />
                      {t('rememberMe')}
                    </Flex>
                  </Text>
                  <Text size="2">
                    <a href="#" style={{ color: "var(--red-9)", fontWeight: 500 }}>
                      {t('forgotPassword')}
                    </a>
                  </Text>
                </Flex>

                <Button type="submit" disabled={loading} size="3" style={{ width: "100%" }}>
                  {loading ? <LoadingSpinner size="1" /> : t('signIn')}
                </Button>
              </Flex>
            </form>

            <Separator size="4" my="4" />

            <Flex direction="column" align="center" gap="4">
              <Text size="2" color="gray">{t('staffMember')}</Text>
              <Link to="/restaurant/staff/login" style={{ color: "var(--red-9)", fontWeight: 500 }}>
                {t('loginAsStaff')}
              </Link>
            </Flex>

            <Separator size="4" my="4" />

            <Flex direction="column" align="center" gap="4">
              <Text size="2" color="gray">{t('newToPlatform')}</Text>
              <Link to="/restaurant/register" style={{ color: "var(--red-9)", fontWeight: 500 }}>
                {t('registerRestaurant')}
              </Link>
            </Flex>
          </Card>

          <Flex justify="center">
            <Link to="/" style={{ fontSize: "var(--font-size-2)", color: "var(--gray-11)" }}>
              {language === 'ar' ? 'العودة للموقع الرئيسي ←' : '← Back to main site'}
            </Link>
          </Flex>
        </Flex>
      </Flex>
  )
}

export default RestaurantLoginPage 